import { useEffect } from 'react';

const PROD_ORIGIN = 'https://aqualityhandyman.com';
const DEFAULT_OG_IMAGE = `${PROD_ORIGIN}/og-card.jpg`;

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

// Lightweight SEO manager: sets unique <title>, meta description, canonical, OG/Twitter tags and JSON-LD per page.
export const Seo = ({ title, description, path, jsonLd, image, noindex = false }) => {
  useEffect(() => {
    if (title) document.title = title;
    const canonical = path ? `${PROD_ORIGIN}${path}` : `${PROD_ORIGIN}${window.location.pathname}`;
    const ogImage = image || DEFAULT_OG_IMAGE;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:image:alt', 'A Quality Handyman mascot — Albuquerque, NM handyman services');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);
    setLink('canonical', canonical);
    // Single robots meta: index.html ships a default "index, follow"; noindex pages override it, others restore it.
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta'); robots.setAttribute('name', 'robots'); document.head.appendChild(robots);
    }
    if (!robots.dataset.defaultContent) robots.dataset.defaultContent = robots.getAttribute('content') || 'index, follow';
    robots.setAttribute('content', noindex ? 'noindex, nofollow' : robots.dataset.defaultContent);
  }, [title, description, path, image, noindex]);

  // Manage a per-page JSON-LD structured-data block (added on mount, removed on unmount)
  useEffect(() => {
    if (!jsonLd) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-jsonld', 'true');
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [jsonLd]);

  return null;
};

export default Seo;
